import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import styles from './CustomModal.module.css';

const CustomModal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md', // sm, md, lg, xl, fullscreen
  centered = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  showHeader = true,
  showFooter = false,
  footer,
  className = '',
  headerClassName = '',
  bodyClassName = '',
  footerClassName = '',
  animation = 'fade', // fade, slide, zoom
}) => {
  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && closeOnEscape && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, closeOnEscape, onClose]);

  // Handle overlay click
  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };

  // Get size class
  const getSizeClass = () => {
    switch (size) {
      case 'sm':
        return styles.modalSm;
      case 'lg':
        return styles.modalLg;
      case 'xl':
        return styles.modalXl;
      case 'fullscreen':
        return styles.modalFullscreen;
      case 'md':
      default:
        return styles.modalMd;
    }
  };

  // Get animation class
  const getAnimationClass = () => {
    switch (animation) {
      case 'slide':
        return styles.animationSlide;
      case 'zoom':
        return styles.animationZoom;
      case 'fade':
      default:
        return styles.animationFade;
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className={`${styles.overlay} ${isOpen ? styles.overlayVisible : ''}`}
      onClick={handleOverlayClick}
    >
      <div
        className={`
          ${styles.modal} 
          ${getSizeClass()} 
          ${getAnimationClass()} 
          ${centered ? styles.centered : ''} 
          ${className}
        `}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {showHeader && (
          <div className={`${styles.header} ${headerClassName}`}>
            <h2 id="modal-title" className={styles.title}>
              {title}
            </h2>
            {showCloseButton && (
              <button
                type="button"
                className={styles.closeBtn}
                onClick={onClose}
                aria-label="Close modal"
              >
                <FaTimes />
              </button>
            )}
          </div>
        )}

        <div className={`${styles.body} ${bodyClassName}`}>
          {children}
        </div>

        {showFooter && footer && (
          <div className={`${styles.footer} ${footerClassName}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Render modal in portal
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default CustomModal;
